import { useRef, useEffect, useState, useCallback } from 'react'

// RichEditor — The Margins
// Floating selection toolbar, full formatting suite, clean writing experience.
// Uses contentEditable + execCommand (no external deps, keeps bundle tiny).

const TOOLBAR_TOOLS = [
  { label: 'B',   cmd: 'bold',        title: 'Bold (Ctrl/⌘ B)',        style: { fontWeight: 700 } },
  { label: 'I',   cmd: 'italic',      title: 'Italic (Ctrl/⌘ I)',      style: { fontStyle: 'italic' } },
  { label: 'U',   cmd: 'underline',   title: 'Underline (Ctrl/⌘ U)',   style: { textDecoration: 'underline' } },
  { sep: true },
  { label: 'H2',  cmd: 'formatBlock', val: 'h2',         title: 'Heading 2' },
  { label: 'H3',  cmd: 'formatBlock', val: 'h3',         title: 'Heading 3' },
  { label: '❝',   cmd: 'formatBlock', val: 'blockquote', title: 'Pull Quote' },
  { label: '¶',   cmd: 'formatBlock', val: 'p',          title: 'Paragraph' },
  { sep: true },
  { label: '—',   cmd: 'insertHorizontalRule', title: 'Divider' },
  { label: '🔗',  cmd: 'link',        title: 'Insert Link (Ctrl/⌘ K)' },
]

export default function RichEditor({ value, onChange, placeholder, style = {} }) {
  const editorRef = useRef(null)
  const initialised = useRef(false)
  const [toolbar, setToolbar] = useState({ visible: false, x: 0, y: 0, activeFormats: {} })
  const [wordCount, setWordCount] = useState(0)
  const [charCount, setCharCount] = useState(0)
  const toolbarRef = useRef(null)

  useEffect(() => {
    if (editorRef.current && !initialised.current) {
      editorRef.current.innerHTML = value || ''
      initialised.current = true
      updateCounts(value || '')
    }
  }, [])

  useEffect(() => {
    if (editorRef.current && initialised.current && value === '') {
      editorRef.current.innerHTML = ''
      setWordCount(0)
      setCharCount(0)
    }
  }, [value])

  function updateCounts(html) {
    const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    setCharCount(text.length)
    setWordCount(text ? text.split(/\s+/).filter(Boolean).length : 0)
  }

  function exec(command, val = null) {
    editorRef.current?.focus()
    if (command === 'link') {
      const url = window.prompt('Enter URL:', 'https://')
      if (url && url !== 'https://') {
        document.execCommand('createLink', false, url)
      }
      sync()
      return
    }
    document.execCommand(command, false, val)
    sync()
  }

  function sync() {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML
      onChange(html)
      updateCounts(html)
    }
  }

  function getActiveFormats() {
    const formats = {}
    try {
      formats.bold = document.queryCommandState('bold')
      formats.italic = document.queryCommandState('italic')
      formats.underline = document.queryCommandState('underline')
    } catch {}
    return formats
  }

  const updateFloatingToolbar = useCallback(() => {
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed || !selection.toString().trim()) {
      setToolbar(t => ({ ...t, visible: false }))
      return
    }
    const editor = editorRef.current
    if (!editor) return
    let node = selection.anchorNode
    while (node && node !== editor) node = node.parentNode
    if (!node) { setToolbar(t => ({ ...t, visible: false })); return }

    const range = selection.getRangeAt(0)
    const rect = range.getBoundingClientRect()
    const editorRect = editor.getBoundingClientRect()
    const x = rect.left - editorRect.left + rect.width / 2
    const y = rect.top - editorRect.top - 52

    setToolbar({
      visible: true,
      x: Math.max(90, Math.min(x, editorRect.width - 90)),
      y: Math.max(8, y),
      activeFormats: getActiveFormats(),
    })
  }, [])

  function handleKeyDown(e) {
    const mod = e.ctrlKey || e.metaKey
    if (e.key === 'Tab') { e.preventDefault(); exec('insertText', '    '); return }
    if (mod && e.key === 'b') { e.preventDefault(); exec('bold'); return }
    if (mod && e.key === 'i') { e.preventDefault(); exec('italic'); return }
    if (mod && e.key === 'u') { e.preventDefault(); exec('underline'); return }
    if (mod && e.key === 'k') { e.preventDefault(); exec('link'); return }
  }

  function handleMouseUp() { setTimeout(updateFloatingToolbar, 10) }
  function handleKeyUp() { sync(); setTimeout(updateFloatingToolbar, 10) }

  function handleMouseDown(e) {
    if (toolbarRef.current?.contains(e.target)) { e.preventDefault(); return }
  }

  return (
    <div style={{ position: 'relative', ...editorStyles.wrap }} onMouseDown={handleMouseDown}>

      {toolbar.visible && (
        <div
          ref={toolbarRef}
          style={{
            ...editorStyles.floatingToolbar,
            left: toolbar.x,
            top: toolbar.y,
            transform: 'translateX(-50%)',
          }}
        >
          {TOOLBAR_TOOLS.map((t, i) => {
            if (t.sep) return <span key={i} style={editorStyles.floatSep} />
            const isActive = (t.cmd === 'bold' && toolbar.activeFormats.bold) ||
                             (t.cmd === 'italic' && toolbar.activeFormats.italic) ||
                             (t.cmd === 'underline' && toolbar.activeFormats.underline)
            return (
              <button
                key={i}
                title={t.title}
                onMouseDown={e => { e.preventDefault(); exec(t.cmd, t.val || null) }}
                style={{ ...editorStyles.floatBtn, ...(isActive ? editorStyles.floatBtnActive : {}), ...(t.style || {}) }}
              >
                {t.label}
              </button>
            )
          })}
        </div>
      )}

      <div style={editorStyles.toolbar}>
        <div style={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {TOOLBAR_TOOLS.map((t, i) => {
            if (t.sep) return <span key={i} style={{ width: 1, height: 16, background: 'var(--rule)', margin: '0 4px', display: 'inline-block' }} />
            return (
              <button
                key={i}
                type="button"
                title={t.title}
                onMouseDown={e => { e.preventDefault(); exec(t.cmd, t.val || null) }}
                style={{ ...editorStyles.toolBtn, ...(t.style || {}) }}
              >
                {t.label}
              </button>
            )
          })}
        </div>
        <span style={editorStyles.toolbarHint}>select text for quick toolbar  ·  Ctrl/⌘ B  I  K</span>
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={sync}
        onBlur={sync}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onMouseUp={handleMouseUp}
        data-placeholder={placeholder}
        style={{ ...editorStyles.editor, ...style }}
        className="rich-editor"
      />

      <div style={editorStyles.footer}>
        <span>{wordCount} word{wordCount !== 1 ? 's' : ''}</span>
        <span style={{ color: 'var(--rule)', margin: '0 6px' }}>·</span>
        <span>{charCount} chars</span>
        <span style={{ color: 'var(--rule)', margin: '0 6px' }}>·</span>
        <span>~{Math.max(1, Math.ceil(wordCount / 200))} min read</span>
      </div>
    </div>
  )
}

const editorStyles = {
  wrap: {
    border: '1px solid var(--rule)', borderRadius: 12,
    background: 'var(--paper)', display: 'flex', flexDirection: 'column',
    overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.12)',
  },
  toolbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '2px', padding: '0.65rem 1rem',
    borderBottom: '1px solid var(--rule)', background: 'var(--cream)', flexWrap: 'wrap',
    borderRadius: '12px 12px 0 0',
  },
  toolBtn: {
    background: 'none', border: '1px solid transparent', padding: '0.28rem 0.6rem',
    fontFamily: "'DM Mono', monospace", fontSize: '0.7rem', fontWeight: 700,
    cursor: 'pointer', color: 'var(--ink)', transition: 'all 0.15s', borderRadius: 6, lineHeight: 1.2,
  },
  toolbarHint: { fontFamily: "'DM Mono', monospace", fontSize: '0.52rem', color: 'var(--muted)', letterSpacing: '0.04em', whiteSpace: 'nowrap' },
  editor: {
    minHeight: 520, padding: '2rem 2.5rem', outline: 'none',
    fontSize: '1.08rem', lineHeight: 1.95, color: 'var(--ink)',
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 400, flex: 1,
  },
  footer: {
    display: 'flex', alignItems: 'center', padding: '0.6rem 1rem',
    borderTop: '1px solid var(--rule)', background: 'var(--cream)',
    fontFamily: "'DM Mono', monospace", fontSize: '0.54rem', color: 'var(--muted)',
    letterSpacing: '0.06em', borderRadius: '0 0 12px 12px',
  },
  floatingToolbar: {
    position: 'absolute', display: 'flex', alignItems: 'center', gap: 2,
    background: '#1a1714', border: '1px solid #3a3530',
    padding: '0.4rem 0.6rem', zIndex: 1000,
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)', borderRadius: 8, pointerEvents: 'all',
  },
  floatBtn: {
    background: 'none', border: '1px solid transparent', padding: '0.22rem 0.5rem',
    fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', fontWeight: 700,
    cursor: 'pointer', color: '#e8e0d4', transition: 'all 0.1s', borderRadius: 4, lineHeight: 1.2,
  },
  floatBtnActive: { background: 'rgba(200,64,26,0.2)', color: '#C8401A', borderColor: 'rgba(200,64,26,0.3)' },
  floatSep: { display: 'inline-block', width: 1, height: 14, background: '#3a3530', margin: '0 4px' },
}
